import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';
export class ResourceService {
    /**
     * Get Nango Connect Session Token
     * Gets a unique connect session token for authentication with nango.
     * See https://docs.nango.dev/guides/api-authorization/authorize-in-your-app-default-ui
     * for details.
     * @param methodIdentifier
     * @returns NangoConnectSessionTokenResponse Successful Response
     * @throws ApiError
     */
    static getNangoConnectSessionToken(methodIdentifier) {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/resource/nango/{method_identifier}',
            path: {
                'method_identifier': methodIdentifier,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get Available Integrations
     * Retrieve all the integration definitions that this user has access to.
     *
     * This will be the set of builtins, along with whatever the user has manually
     * defined.
     * @returns IntegrationDefinition Successful Response
     * @throws ApiError
     */
    static getAvailableIntegrations() {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/resource/integration',
        });
    }
    /**
     * Add Integration
     * Add a manual integration definition to the user.
     *
     * The user may then use this to create a resource of a particular type.
     * @param requestBody
     * @returns UserIntegrationDefinition Successful Response
     * @throws ApiError
     */
    static addIntegration(requestBody) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/resource/integration',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get All Integrations
     * Retrieve all the integration definitions.
     * @returns IntegrationDefinition Successful Response
     * @throws ApiError
     */
    static getAllIntegrations() {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/resource/integration/all',
        });
    }
    /**
     * Redirect Icon To Static
     * Redirect icon requests with a resource key to the actual icon file.
     * @param resourceKey
     * @returns any Successful Response
     * @throws ApiError
     */
    static redirectIconToStatic(resourceKey) {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/resource/integration/icon/{resource_key}',
            path: {
                'resource_key': resourceKey,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Reset All Integrations
     * Resets all integrations, and rebuilds them from `integration.yml`.
     * @returns any Successful Response
     * @throws ApiError
     */
    static resetAllIntegrations() {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/resource/integration/reset',
        });
    }
    /**
     * Get Provider Apis
     * @param provider
     * @returns APIWithScopes Successful Response
     * @throws ApiError
     */
    static getProviderApis(provider) {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/resource/integration/oauth/{provider}',
            path: {
                'provider': provider,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Start Oauth Flow
     * @param requestBody
     * @returns OAuthStartResponse Successful Response
     * @throws ApiError
     */
    static startOauthFlow(requestBody) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/resource/integration/oauth',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get Resources
     * Get all resources that this user has added to their account.
     * @returns UserResourceWithIntegration Successful Response
     * @throws ApiError
     */
    static getResources() {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/resource',
        });
    }
    /**
     * Add Resource
     * Add a resource to the user's collection.
     * @param requestBody
     * @returns UserResource Successful Response
     * @throws ApiError
     */
    static addResource(requestBody) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/resource',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Delete Resource
     * Delete the resources with the given ID.
     *
     * In the future, this will have to be hooked up to OAuth deregistration flow.
     * @param rscId
     * @returns any Successful Response
     * @throws ApiError
     */
    static deleteResource(rscId) {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/resource/{rsc_id}',
            path: {
                'rsc_id': rscId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get Resource By Id
     * @param rscId
     * @returns UserResourceWithIntegration Successful Response
     * @throws ApiError
     */
    static getResourceById(rscId) {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/resource/{rsc_id}',
            path: {
                'rsc_id': rscId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get Resource By Id For Deployment
     * Retrieve a resource by id and via authorized token.
     *
     * This will be called only be the code deployments.
     *
     * Make sure the resource id specified is present in the token.
     * Additionally, we should make sure that the token wasn't otherwise
     * blacklisted or the resource requested is no longer authorized for this
     * user.
     * @param rscId
     * @returns ResourceResponse Successful Response
     * @throws ApiError
     */
    static getResourceByIdForDeployment(rscId) {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/resource/deployment/{rsc_id}',
            path: {
                'rsc_id': rscId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
}
